import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface Lead {
  id: string;
  companyId: string;
  eventId: string;
  userId: string;
  scoreBand: 'A' | 'B' | 'C' | 'D';
  scoreNumeric: number;
  aiSummary: string;
  aiSummaryZh?: string;
  recommendedAction: string;
  recommendedActionZh?: string;
  ocrImageUri: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  contact: {
    id: string;
    leadId: string;
    fullName: string;
    email: string;
    phone: string;
    title: string;
  };
  entity: {
    companyName: string;
    website: string;
    country: string;
    incorporationYear: number | null;
    status: string;
    employees: string | null;
    revenue: string | null;
    industry: string | null;
    jurisdiction: string | null;
    geographicMarkets: string[];
    industryRelevance: string | null;
    industryTags: string[];
  };
  credit: {
    creditRating: string | null;
    paymentBehavior: string | null;
    creditLimitIndicator: string | null;
  };
  risk: {
    riskScore: number;
    riskTier: string;
    identityConfidence: number;
    fraudFlags: {
      id: string;
      name: string;
      severity: string;
      description: string;
    }[];
  };
}

export function useLeads(eventId: string | null) {
  return useQuery({
    queryKey: ['leads', eventId],
    queryFn: () => apiFetch<{ leads: Lead[] }>(`/api/leads?eventId=${eventId}`).then((d) => d.leads),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

export function useLeadDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => apiFetch<Lead>(`/api/leads/${id}`),
    enabled: !!id,
  });
}

export function useUpdateNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, notes }: { leadId: string; notes: string }) =>
      apiFetch(`/api/leads/${leadId}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ notes }),
      }),
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSubmitManualLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: {
      fullName: string;
      companyName: string;
      email?: string;
      phone?: string;
      website?: string;
      title?: string;
      country?: string;
      eventId: string;
    }) =>
      apiFetch<{ leadId: string; enrichmentJobId: string }>('/api/leads/manual', {
        method: 'POST',
        body: JSON.stringify(fields),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['eventStats'] });
    },
  });
}

export function useScanCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageBase64, eventId }: { imageBase64: string; eventId: string }) =>
      apiFetch<{ leadId: string; enrichmentJobId: string }>('/api/leads/scan', {
        method: 'POST',
        body: JSON.stringify({ imageBase64, eventId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['eventStats'] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      apiFetch(`/api/leads/${leadId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['eventStats'] });
    },
  });
}

export function useBulkDeleteLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch(`/api/leads/batch`, { method: 'DELETE', body: JSON.stringify({ ids }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['eventStats'] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      leadId,
      contact,
      entity,
    }: {
      leadId: string;
      contact?: { fullName?: string; email?: string; phone?: string; title?: string };
      entity?: { companyName?: string; country?: string; website?: string };
    }) =>
      apiFetch<Lead>(`/api/leads/${leadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ contact, entity }),
      }),
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}
